// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// One-off run: choose blueprint and version, upload, follow status (requirements 5.1-5.4). With blueprint sets
// enabled, an author can instead run the document against a Blueprint_Set's draft or one of its versions
// (blueprint-sets Req 4.1, 12.3).
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FileUpload from '@cloudscape-design/components/file-upload';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { UPLOAD_LIMITS_TEXT, type VersionRef } from '@bda/shared';
import { useBlueprint, useBlueprints, useSet, useSets } from '../../api/hooks';
import { canAuthor, useAuth } from '../../auth/AuthProvider';
import { setsEnabled } from '../../config';
import { ErrorDetail } from '../../lib/notify';
import { startDocumentRun } from './runFlow';
import { useFileCheck } from '../../lib/pageCount';

/** What the run uses: one blueprint (as before sets) or a Blueprint_Set. */
type RunWith = 'blueprint' | 'set';

export const RUN_WITH_LABEL = 'Run with';
export const DRAFT_SET_OPTION = 'Draft set';

const parseRef = (v: string): VersionRef => (v === 'DRAFT' ? 'DRAFT' : Number(v));

export function RunPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Set choices only for authors in a deployment with sets turned on (Req 12.3, 14.2); everyone else sees the
  // single-blueprint page exactly as before.
  const setsAllowed = setsEnabled() && canAuthor(user);
  // `?setId=` preselects the set (the set page's "Run a document"); it is ignored unless sets are allowed.
  const [runWith, setRunWith] = useState<RunWith>(params.get('setId') ? 'set' : 'blueprint');
  const [blueprintId, setBlueprintId] = useState<string | null>(params.get('blueprintId'));
  const [version, setVersion] = useState<VersionRef | null>(null);
  const [setId, setSetId] = useState<string | null>(params.get('setId'));
  const [chosenSetVersion, setChosenSetVersion] = useState<VersionRef | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const list = useBlueprints({ archived: 'false' });
  const detail = useBlueprint(blueprintId ?? undefined);
  const sets = useSets({ archived: 'false' }, { enabled: setsAllowed });
  const setDetail = useSet(setsAllowed && setId ? setId : undefined);
  const isSet = setsAllowed && runWith === 'set';

  const bpOptions = (list.data?.items ?? []).map((b) => ({
    value: b.id,
    label: b.name,
    description: b.documentType,
  }));
  const versionOptions = useMemo(
    () => [
      { value: 'DRAFT', label: 'Current draft', description: 'Results are labelled "Draft blueprint"' },
      ...(detail.data?.versions ?? []).map((v) => ({
        value: String(v.version),
        label: `Version ${v.version}`,
        description: v.changeNote || undefined,
      })),
    ],
    [detail.data],
  );
  const effectiveVersion: VersionRef =
    version ?? (detail.data?.blueprint.latestVersion ? detail.data.blueprint.latestVersion : 'DRAFT');

  const setOptions = (sets.data?.items ?? []).map((s) => ({
    value: s.id,
    label: s.name,
    description: `${s.memberCount} ${s.memberCount === 1 ? 'blueprint' : 'blueprints'}`,
  }));
  const setVersionOptions = useMemo(
    () => [
      { value: 'DRAFT', label: DRAFT_SET_OPTION, description: `Results are labelled "${DRAFT_SET_OPTION}"` },
      ...(setDetail.data?.versions ?? []).map((v) => ({
        value: String(v.version),
        label: `Version ${v.version}`,
        description: v.changeNote || undefined,
      })),
    ],
    [setDetail.data],
  );
  // The latest published Set_Version is preselected when one exists, as for blueprints.
  const effectiveSetVersion: VersionRef = chosenSetVersion ?? setDetail.data?.set.latestVersion ?? 'DRAFT';
  // Document_Type conflicts of the Set_Draft reject a draft run (Req 2.7, 4.8); say so before the upload.
  const draftConflicts = isSet && effectiveSetVersion === 'DRAFT' ? (setDetail.data?.conflicts ?? []) : [];

  const file = files[0];
  const { error: fileError, checking } = useFileCheck(file);
  const target = isSet ? setId : blueprintId;

  const submit = async () => {
    if (!target || !file) return;
    setError(null);
    setProgress(0);
    try {
      const runId = isSet
        ? await startDocumentRun({
            file,
            mode: 'set',
            setId: setId!,
            setVersion: effectiveSetVersion,
            onProgress: setProgress,
          })
        : await startDocumentRun({
            file,
            mode: 'custom',
            blueprintId: blueprintId!,
            version: effectiveVersion,
            onProgress: setProgress,
          });
      navigate(`/runs/${runId}`);
    } catch (e) {
      setError(e);
      setProgress(null);
    }
  };

  return (
    <ContentLayout
      header={
        <Header variant="h1" description="Run one document through a blueprint and review the extracted fields.">
          Run a document
        </Header>
      }
    >
      <Form
        actions={
          <Button
            variant="primary"
            loading={progress !== null}
            disabled={!target || !file || !!fileError || checking}
            onClick={() => void submit()}
          >
            Run
          </Button>
        }
        errorText={error !== null ? <ErrorDetail error={error} action="Start run" /> : undefined}
      >
        <Container>
          <SpaceBetween size="l">
            {setsAllowed && (
              <FormField label={RUN_WITH_LABEL}>
                <SegmentedControl
                  label={RUN_WITH_LABEL}
                  selectedId={runWith}
                  options={[
                    { id: 'blueprint', text: 'Blueprint' },
                    { id: 'set', text: 'Blueprint set' },
                  ]}
                  onChange={(e) => setRunWith(e.detail.selectedId as RunWith)}
                />
              </FormField>
            )}
            {isSet ? (
              <>
                <FormField label="Blueprint set">
                  <Select
                    selectedOption={setOptions.find((o) => o.value === setId) ?? null}
                    options={setOptions}
                    placeholder="Choose a blueprint set"
                    empty="No blueprint sets"
                    statusType={sets.isLoading ? 'loading' : 'finished'}
                    filteringType="auto"
                    onChange={(e) => {
                      setSetId(e.detail.selectedOption.value ?? null);
                      setChosenSetVersion(null);
                    }}
                  />
                </FormField>
                {setId && (
                  <FormField label="Set version">
                    <Select
                      selectedOption={
                        setVersionOptions.find((o) => o.value === String(effectiveSetVersion)) ?? setVersionOptions[0]!
                      }
                      options={setVersionOptions}
                      onChange={(e) => setChosenSetVersion(parseRef(e.detail.selectedOption.value!))}
                    />
                  </FormField>
                )}
              </>
            ) : (
              <>
                <FormField label="Blueprint">
                  <Select
                    selectedOption={bpOptions.find((o) => o.value === blueprintId) ?? null}
                    options={bpOptions}
                    placeholder="Choose a blueprint"
                    statusType={list.isLoading ? 'loading' : 'finished'}
                    filteringType="auto"
                    onChange={(e) => {
                      setBlueprintId(e.detail.selectedOption.value ?? null);
                      setVersion(null);
                    }}
                  />
                </FormField>
                {blueprintId && (
                  <FormField label="Version">
                    <Select
                      selectedOption={
                        versionOptions.find((o) => o.value === String(effectiveVersion)) ?? versionOptions[0]!
                      }
                      options={versionOptions}
                      onChange={(e) => {
                        const v = e.detail.selectedOption.value!;
                        setVersion(v === 'DRAFT' ? 'DRAFT' : Number(v));
                      }}
                    />
                  </FormField>
                )}
              </>
            )}
            <FormField
              label="Document"
              description={
                isSet
                  ? `${UPLOAD_LIMITS_TEXT} BDA splits the packet into documents and matches each one to a blueprint of the set.`
                  : `${UPLOAD_LIMITS_TEXT} Documents over 20 pages are processed in segments of up to 20 pages.`
              }
              errorText={fileError}
            >
              <FileUpload
                value={files}
                onChange={(e) => setFiles(e.detail.value)}
                accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
                showFileSize
                i18nStrings={{
                  uploadButtonText: () => 'Choose file',
                  dropzoneText: () => 'Drop a file to upload',
                  removeFileAriaLabel: () => 'Remove file',
                  limitShowFewer: 'Show fewer',
                  limitShowMore: 'Show more',
                  errorIconAriaLabel: 'Error',
                  warningIconAriaLabel: 'Warning',
                }}
              />
            </FormField>
            {progress !== null && <ProgressBar value={Math.round(progress * 100)} label="Uploading" />}
            {!isSet && effectiveVersion === 'DRAFT' && blueprintId && (
              <Alert type="info">This runs the current draft, not a published version.</Alert>
            )}
            {isSet && setId && effectiveSetVersion === 'DRAFT' && (
              <Alert type="info">This runs the set's current draft, not a published version.</Alert>
            )}
            {draftConflicts.map((c) => (
              <Alert key={c.message} type="warning" header="Document type conflict">
                {c.message} Runs of the draft set are rejected until the conflict is resolved.
              </Alert>
            ))}
          </SpaceBetween>
        </Container>
      </Form>
    </ContentLayout>
  );
}
